import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Badge } from 'antd';
import { SearchOutlined, FilterOutlined, MenuOutlined, EnvironmentOutlined, CloseCircleFilled } from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import { getEmployeeForMapQuery } from '../../util/query/employee.query';
import "./mapDashboard.css";
import {skillQuery} from "@src/util/query/skill.query.js"
import { useNavigate } from 'react-router-dom';
import { ArrowUpRightFromSquare, Users } from 'lucide-react';
import Spinner from "@src/components/spinner";


const MapDashboard = () => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const markersRef = useRef([]);
  const clustersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const isZoomingRef = useRef(false);


  
  // Search functionality
  const [searchValue, setSearchValue] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchInputRef = useRef(null);

  const [licenceTypes, setLicenceType] = useState([]);
  
  // skill query  
  const {data: skillData, loading: skillDataLoading, refetch: skillRefetch} = useQuery(skillQuery,{
      fetchPolicy: 'network-only'
  }); 

  useEffect(()=>{
    if(skillData?.getSkills){
      setLicenceType(skillData?.getSkills?.filter((sk)=> sk?.color!==null)?.map((skill, index)=>{
        return{
          id: skill?._id,
          name: skill?.skill,
          color: skill?.color,
          default: index==0? true : false
        }
      }));
    }
  },[skillData?.getSkills]);

  const [getEmployeeForMap, {data, loading, refetch}] = useLazyQuery(getEmployeeForMapQuery);

  useEffect(()=>{
    getEmployeeForMap({
      fetchPolicy: 'network-only'
    })
  },[]);

  const [activeFilters, setActiveFilters] = useState([]);
  
  const activeFiltersRef = useRef(activeFilters);
  const licenceTypesRef = useRef(licenceTypes);
  
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);
  
  useEffect(() => {
    licenceTypesRef.current = licenceTypes;
  }, [licenceTypes]);
  
  useEffect(()=>{
    if(licenceTypes?.length>0){
      const defaultFilter = licenceTypes.find(t => t.default);
      setActiveFilters(defaultFilter ? [defaultFilter.id] : []);
    }
  },[licenceTypes]);

  const locations = useRef([]);

  const getDistanceInMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  };

  const resolveEmployeeSkill = (employee, activeFilters) => {
    if (!employee?.employeeskillDetail?.length) return null;
  
    const matched = employee.employeeskillDetail.find(esd =>
      activeFilters.includes(esd.skill)
    );
    return matched || employee.employeeskillDetail[0];
  };

  // Initialize Google Places services
  useEffect(() => {
    if (window.google && map) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(map);
      
    }
  }, [map]);

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!value.trim()) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      if (autocompleteServiceRef.current && value.trim()) {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: value,
            componentRestrictions: { country: 'uk' }, // Restrict to UK
            types: ['geocode', 'establishment'] // Include cities, addresses, and places
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setPredictions(predictions);
              setShowPredictions(true);
            } else {
              setPredictions([]);
              setShowPredictions(false);
            }
          }
        );
      }
    }, 300); // 300ms debounce
  };

  // Handle place selection
  const handlePlaceSelect = (placeId) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId: placeId,
        fields: ['geometry', 'name', 'formatted_address']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
          const location = place.geometry.location;
          
          // Clear predictions and close dropdown
          setShowPredictions(false);
          setPredictions([]);
          setSearchValue(place.name || place.formatted_address);

          // Animate to the location
          animateToLocation(location.lat(), location.lng());
        }
      }
    );
  };

  // Smooth animation to location
  const animateToLocation = (targetLat, targetLng) => {
    if (!map) return;

    const startPosition = map.getCenter();
    const startZoom = map.getZoom();
    const targetZoom = 14; // Zoom level for search results

    isZoomingRef.current = true;

    // Pan to location first
    map.panTo({ lat: targetLat, lng: targetLng });

    // Then smoothly zoom
    setTimeout(() => {
      if (startZoom < targetZoom) {
        const zoomDiff = targetZoom - startZoom;
        const steps = Math.min(Math.abs(zoomDiff), 8);
        const zoomStep = zoomDiff / steps;
        
        let currentStep = 0;
        const zoomInterval = setInterval(() => {
          currentStep++;
          if (currentStep <= steps) {
            map.setZoom(startZoom + (zoomStep * currentStep));
          } else {
            clearInterval(zoomInterval);
            setTimeout(() => {
              isZoomingRef.current = false;
              updateClusters(map);
            }, 150);
          }
        }, 60);
      } else {
        map.setZoom(targetZoom);
        setTimeout(() => {
          isZoomingRef.current = false;
          updateClusters(map);
        }, 150);
      }
    }, 300);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchValue('');
    setPredictions([]);
    setShowPredictions(false);
  };

  // Close predictions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowPredictions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const handleEmployeeClick = (id)=>{
    window.open("/user/employee-detail/"+id);
  };


  useEffect(()=>{

    const emp = data?.getEmployeeForMap?.response;
    
    if (!emp || emp.length === 0) {
      setIsMapLoading(false);
      return;
    }

    locations.current = [];

    for (let i = 0; i < emp.length; i++) {
      if (emp[i]?.location?.coordinates?.[0] && emp[i]?.location?.coordinates?.[1]) {
        
        const selectedSkill = resolveEmployeeSkill(emp[i], activeFilters);
        const otherSkill = emp[i]?.employeeskillDetail?.filter((skill)=> skill?._id!==selectedSkill?._id)?.map((sk)=> sk?.skillDetail?.skill);

        locations.current.push({
          lat: emp[i]?.location?.coordinates[1],
          lng: emp[i]?.location?.coordinates[0],
          id: emp[i]?._id || i,
          licenceType: selectedSkill?.skill,
          licenceName: selectedSkill?.skillDetail?.skill || 'Unknown',
          firstName: emp[i]?.firstname || 'Unknown',
          lastName: emp[i]?.lastname || '',
          imageURL: emp[i]?.metadata?.imageURL || null,
          employee: emp[i],
          otherSkill: otherSkill,
        });
      }
    }

    if (map) {

      markersRef.current.forEach(m => m.marker.setMap(null));
      markersRef.current = [];

      locations.current.forEach(loc => {
        const marker = new window.google.maps.Marker({
          position: { lat: loc.lat, lng: loc.lng },
          map: null
        });
        markersRef.current.push({ marker, location: loc });
      });

      updateClusters(map);
      setIsMapLoading(false);

    }

  },[data?.getEmployeeForMap?.response, map, activeFilters]);

  useEffect(() => {
    if (window.google) {
      initMap();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_MAP}&libraries=places`;
    script.async = true;
    script.onload = initMap;
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const initMap = () => {
    if (!window.google || !mapRef.current) return;

    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: { lat: 54.5, lng: -2.5 },
      zoom: 6,
      styles: [
        { elementType: 'geometry', stylers: [{ color: '#4a5568' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#1f2937' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#374151' }] },
        { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#6b7280' }] },
        { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#6b7280' }] },
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        },
        {
          featureType: 'poi.business',
          stylers: [{ visibility: 'off' }]
        }
      ],
      disableDefaultUI: true
    });

    infoWindowRef.current = new window.google.maps.InfoWindow({
      content: '',
      disableAutoPan: false
    });



    setMap(mapInstance);

    mapInstance.addListener('idle', () => {
      if (!isZoomingRef.current) {
        updateClusters(mapInstance);
      }
    });
    
    setIsMapLoading(false);
  };

  

  const createEmployeeInfoWindow = (location) => {
    const licenceInfo = licenceTypesRef.current.find(t => t.id === location.licenceType);
    const fullName = `${location.firstName} ${location.lastName}`.trim();
    console.log(location?.employee, "employee")
    return `
      <div class="info-card" data-id="${location.id}">
        <div class="info-card-content">
          <h3 class="info-card-name">${fullName}  
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              width="30"
              height="30"
              fill="white"
            >
              <path d="M320 0C302.3 0 288 14.3 288 32s14.3 32 32 32h82.7L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3V192c0 17.7 14.3 32 32 32s32-14.3 32-32V32c0-17.7-14.3-32-32-32H320z"/>
              <path d="M80 64C35.8 64 0 99.8 0 144v288c0 44.2 35.8 80 80 80h288c44.2 0 80-35.8 80-80V256c0-17.7-14.3-32-32-32s-32 14.3-32 32v176c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V144c0-8.8 7.2-16 16-16h176c17.7 0 32-14.3 32-32s-14.3-32-32-32H80z"/>
            </svg>

          </h3>
          
          <div class="info-card-licence">
            <div class="info-card-licence-dot" style="background: ${licenceInfo?.color || '#06b6d4'}; box-shadow: 0 0 16px ${licenceInfo?.color || '#06b6d4'}60;"></div>
            <span class="info-card-licence-text">${licenceInfo?.name || 'Unknown Licence'}</span>
          </div>
          
          <div class="info-card-location-box">
            <div class="info-card-location-label">

              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 640"
                width="30"
                height="30"
                fill="white"
              >
                <path d="M271.2 56C265.1 49.8 256.2 47.3 247.8 49.6C239.4 51.9 232.9 58.4 230.8 66.8L215.5 127C214.4 131.4 209.9 134 205.6 132.7L145.8 115.9C137.4 113.5 128.4 115.9 122.3 122C116.2 128.1 113.8 137.1 116.2 145.5L133.1 205.3C134.3 209.6 131.7 214.1 127.4 215.2L67.1 230.5C58.7 232.6 52.1 239.2 49.8 247.6C47.5 256 50 264.9 56.2 271L100.7 314.3C103.9 317.4 103.9 322.6 100.7 325.8L56.3 369.1C50.1 375.2 47.6 384.1 49.9 392.5C52.2 400.9 58.8 407.4 67.2 409.6L127.4 424.9C131.8 426 134.4 430.5 133.1 434.8L116.2 494.5C113.8 502.9 116.2 511.9 122.3 518C128.4 524.1 137.4 526.5 145.8 524.1L205.6 507.2C209.9 506 214.4 508.6 215.5 512.9L230.8 573.1C232.9 581.5 239.5 588.1 247.9 590.4C256.3 592.7 265.2 590.2 271.3 584L314.6 539.5C317.7 536.3 322.9 536.3 326.1 539.5L369.3 584C375.4 590.2 384.3 592.7 392.7 590.4C401.1 588.1 407.6 581.5 409.8 573.1L425.1 513C426.2 508.6 430.7 506 435 507.3L494.8 524.2C503.2 526.6 512.2 524.2 518.3 518.1C524.4 512 526.8 503 524.4 494.6L507.5 434.8C506.3 430.5 508.9 426 513.2 424.9L573.4 409.6C581.8 407.5 588.4 400.9 590.7 392.5C593 384.1 590.5 375.1 584.3 369.1L539.8 325.8C536.6 322.7 536.6 317.5 539.8 314.3L584.3 271C590.5 264.9 593 256 590.7 247.6C588.4 239.2 581.8 232.7 573.4 230.5L513.2 215.2C508.8 214.1 506.2 209.6 507.5 205.3L524.4 145.5C526.8 137.1 524.4 128.1 518.3 122C512.2 115.9 503.2 113.5 494.8 115.9L435 132.8C430.7 134 426.2 131.4 425.1 127.1L409.8 66.8C407.7 58.4 401.1 51.8 392.7 49.5C384.3 47.2 375.4 49.7 369.3 55.9L326 100.5C322.9 103.7 317.7 103.7 314.5 100.5L271.2 56z"/>
              </svg>

              Other Skills

            </div>

            <div class="info-card-location-coords">
              ${location?.otherSkill?.join(", ") || "none"}
            </div>

          </div>
        </div>
      </div>
    `;
  };
  
  const [empVisible, setEmpVisible] = useState(0);

  const updateClusters = (mapInstance) => {
    if (!window.google || !mapInstance) return;

    const currentFilters = activeFiltersRef.current;
    const currentLicenceTypes = licenceTypesRef.current;

    clustersRef.current.forEach(c => c.setMap(null));
    clustersRef.current = [];
    
    markersRef.current.forEach(m => m.marker.setMap(null));

    const zoom = mapInstance.getZoom();
    const bounds = mapInstance.getBounds();
    if (!bounds) return;

    const visible = markersRef.current.filter(m => 
      bounds.contains(m.marker.getPosition()) &&
      currentFilters.includes(m.location.licenceType)
    );

    setEmpVisible(visible?.length);

    if (visible.length === 0) return;

    let CLUSTER_DISTANCE_METERS;
    if (zoom >= 15) {
      CLUSTER_DISTANCE_METERS = 400;
    } else if (zoom >= 12) {
      CLUSTER_DISTANCE_METERS = 1000;
    } else if (zoom >= 9) {
      CLUSTER_DISTANCE_METERS = 10000;
    } else {
      CLUSTER_DISTANCE_METERS = 50000;
    }
    
    const clustered = new Set();
    const clusters = [];

    visible.forEach((markerObj, i) => {
      if (clustered.has(i)) return;

      const cluster = [markerObj];
      clustered.add(i);
      const pos1 = markerObj.marker.getPosition();

      visible.forEach((other, j) => {
        if (i === j || clustered.has(j)) return;

        const pos2 = other.marker.getPosition();
        
        const distance = getDistanceInMeters(
          pos1.lat(), 
          pos1.lng(), 
          pos2.lat(), 
          pos2.lng()
        );

        if (distance < CLUSTER_DISTANCE_METERS) {
          cluster.push(other);
          clustered.add(j);
        }
      });

      clusters.push(cluster);
    });

    clusters.forEach(cluster => {
      if (cluster.length <= 2) {
        cluster.forEach(({ marker: m, location: loc }) => {
          const licenceColor = currentLicenceTypes.find(t => t.id === loc.licenceType)?.color || '#06b6d4';
          
          if (zoom >= 15) {
            const animatedPin = `
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="60" viewBox="0 0 48 60">
                <defs>
                  <filter id="glow-${loc.id}">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                  <linearGradient id="grad-${loc.id}" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:${licenceColor};stop-opacity:1" />
                    <stop offset="100%" style="stop-color:${licenceColor};stop-opacity:0.7" />
                  </linearGradient>
                </defs>
                
                <ellipse cx="24" cy="56" rx="12" ry="3" fill="rgba(0,0,0,0.3)"/>
                
                <path d="M24 4 C14 4 6 12 6 22 C6 32 24 54 24 54 C24 54 42 32 42 22 C42 12 34 4 24 4 Z" 
                      fill="url(#grad-${loc.id})" 
                      filter="url(#glow-${loc.id})"
                      stroke="white" 
                      stroke-width="2"/>
                
                <circle cx="24" cy="22" r="8" fill="white" opacity="0.9"/>
                
                ${loc.imageURL ? 
                  `<image href="${loc.imageURL}" x="16" y="14" width="16" height="16" clip-path="circle(8px at 8px 8px)"/>` :
                  `<text x="24" y="27" font-size="10" font-weight="bold" fill="${licenceColor}" text-anchor="middle">${loc.firstName.charAt(0)}${loc.lastName.charAt(0)}</text>`
                }
              </svg>
            `;
            
            m.setIcon({
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(animatedPin),
              scaledSize: new window.google.maps.Size(48, 60),
              anchor: new window.google.maps.Point(24, 54)
            });
          } else {
            m.setIcon({
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: licenceColor,
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 3
            });
          }
          
          m.setMap(mapInstance);
          
          window.google.maps.event.clearInstanceListeners(m);
          
          m.addListener('click', () => {
            infoWindowRef.current.setContent(createEmployeeInfoWindow(loc));
            infoWindowRef.current.open(mapInstance, m);
            window.google.maps.event.addListenerOnce(
              infoWindowRef.current,
              'domready',
              () => {
                const card = document.querySelector('.info-card');
          
                if (card) {
                  card.addEventListener('click', () => {
                    const employeeId = card.dataset.id;
                    console.log('Clicked employee:', employeeId);
          
                    // ✅ CALL YOUR FUNCTION HERE
                    handleEmployeeClick(employeeId);
                  });
                }
              }
            );
          });
          
          m.addListener('dblclick', (e) => {
            if (e && e.stop) e.stop();
            
            const targetPosition = m.getPosition();
            const targetZoom = 18;
            const currentZoom = mapInstance.getZoom();
            
            if (currentZoom >= targetZoom) return;
            
            isZoomingRef.current = true;
            
            mapInstance.panTo(targetPosition);
            
            setTimeout(() => {
              const zoomDiff = targetZoom - currentZoom;
              const steps = Math.abs(zoomDiff);
              const zoomStep = zoomDiff / steps;
              
              let currentStep = 0;
              const zoomInterval = setInterval(() => {
                currentStep++;
                if (currentStep <= steps) {
                  mapInstance.setZoom(currentZoom + (zoomStep * currentStep));
                } else {
                  clearInterval(zoomInterval);
                  setTimeout(() => {
                    isZoomingRef.current = false;
                    updateClusters(mapInstance);
                  }, 150);
                }
              }, 80);
            }, 200);
          });
          
          if (zoom >= 15) {
            m.addListener('mouseover', () => {
              m.setAnimation(window.google.maps.Animation.BOUNCE);
              setTimeout(() => m.setAnimation(null), 750);
            });
          }
        });
        
      } else {
        const avgLat = cluster.reduce((sum, m) => sum + m.location.lat, 0) / cluster.length;
        const avgLng = cluster.reduce((sum, m) => sum + m.location.lng, 0) / cluster.length;

        const size = Math.min(70, 50 + cluster.length * 2);
        const fontSize = cluster.length > 99 ? 12 : 16;

        const svg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            <defs>
              <radialGradient id="cluster-grad-${avgLat}">
                <stop offset="0%" style="stop-color:#06b6d4;stop-opacity:0.4" />
                <stop offset="50%" style="stop-color:#06b6d4;stop-opacity:0.7" />
                <stop offset="100%" style="stop-color:#0891b2;stop-opacity:1" />
              </radialGradient>
              <filter id="shadow">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.4"/>
              </filter>
            </defs>
            
            <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 5}" fill="url(#cluster-grad-${avgLat})" opacity="0.2" filter="url(#shadow)"/>
            <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 10}" fill="url(#cluster-grad-${avgLat})" opacity="0.5"/>
            <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 15}" fill="#0891b2" stroke="white" stroke-width="3"/>
            
            <text x="${size/2}" y="${size/2 + fontSize/3}" 
                  font-size="${fontSize}" 
                  fill="white" 
                  text-anchor="middle" 
                  font-weight="bold" 
                  font-family="-apple-system, BlinkMacSystemFont, sans-serif">
              ${cluster.length}
            </text>
          </svg>
        `;

        const clusterMarker = new window.google.maps.Marker({
          position: { lat: avgLat, lng: avgLng },
          map: mapInstance,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
            scaledSize: new window.google.maps.Size(size, size),
            anchor: new window.google.maps.Point(size/2, size/2)
          },
          zIndex: 1000
        });

        clusterMarker.addListener('click', () => {
          const clusterBounds = new window.google.maps.LatLngBounds();
          cluster.forEach(m => clusterBounds.extend(m.marker.getPosition()));
          
          const ne = clusterBounds.getNorthEast();
          const sw = clusterBounds.getSouthWest();
          const clusterSpread = getDistanceInMeters(
            ne.lat(), ne.lng(),
            sw.lat(), sw.lng()
          );
          
          let targetZoom;
          if (clusterSpread < 100) {
            targetZoom = 18;
          } else if (clusterSpread < 500) {
            targetZoom = 17;
          } else if (clusterSpread < 2000) {
            targetZoom = 15;
          } else if (clusterSpread < 10000) {
            targetZoom = 13;
          } else {
            targetZoom = 11;
          }
          
          const currentZoom = mapInstance.getZoom();
          
          if (currentZoom >= targetZoom) return;
          
          isZoomingRef.current = true;
          
          mapInstance.panTo({ lat: avgLat, lng: avgLng });
          
          setTimeout(() => {
            const zoomDiff = targetZoom - currentZoom;
            const steps = Math.max(1, Math.abs(zoomDiff));
            const zoomStep = zoomDiff / steps;
            
            let currentStep = 0;
            const zoomInterval = setInterval(() => {
              currentStep++;
              if (currentStep <= steps) {
                mapInstance.setZoom(currentZoom + (zoomStep * currentStep));
              } else {
                clearInterval(zoomInterval);
                setTimeout(() => {
                  isZoomingRef.current = false;
                  updateClusters(mapInstance);
                }, 150);
              }
            }, 80);
          }, 300);
        });

        clustersRef.current.push(clusterMarker);
      }
    });
  };

  const toggleFilter = (filterId) => {
    map.setCenter({ lat: 54.5, lng: -2.5 });
    map.setZoom(6);
    const isExist = activeFilters?.find((id)=> id==filterId);

    setActiveFilters(
      isExist?
      activeFilters?.filter((id)=> id!==filterId)
      :
      [...activeFilters,  filterId]
    );

  };

  const resetFilters = () => {
    const defaultFilter = licenceTypes.find(t => t.default);
    setActiveFilters(defaultFilter ? [defaultFilter.id] : []);
  };

  useEffect(() => {
    if (map) {
      updateClusters(map);
    }
  }, [activeFilters, licenceTypes, map]);

  const resetMap = () => {
    if (map) {
      resetFilters();
      handleClearSearch();
      map.setCenter({ lat: 54.5, lng: -2.5 });
      map.setZoom(6);
    }
  };

  const navigate = useNavigate();

  return (
    <div style={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column', background: '#111827', }}>
      <div style={{ position: 'absolute', top: '72px', left: '16px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div ref={searchInputRef} style={{ background: '#1f2937', borderRadius: '8px', padding: '8px', width: '384px', position: 'relative' }}>
          <Input
            prefix={<EnvironmentOutlined style={{ color: '#9ca3af' }} />}
            suffix={
              searchValue && (
                <CloseCircleFilled 
                  onClick={handleClearSearch}
                  style={{ color: '#9ca3af', cursor: 'pointer' }}
                />
              )
            }
            placeholder="Search for a location (e.g., Manchester, London)"
            value={searchValue}
            onChange={handleSearchChange}
            onFocus={() => {
              if (predictions.length > 0) {
                setShowPredictions(true);
              }
            }}
            style={{ 
              background: '#374151', 
              border: '1px solid #4b5563',
              color: '#e5e7eb'
            }}
          />
          
          {showPredictions && predictions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              background: '#1f2937',
              borderRadius: '8px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
              border: '1px solid #374151',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000
            }}>
              {predictions.map((prediction) => (
                <div
                  key={prediction.place_id}
                  onClick={() => handlePlaceSelect(prediction.place_id)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #374151',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <EnvironmentOutlined style={{ color: '#06b6d4', fontSize: '16px', marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        color: '#e5e7eb',
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '2px'
                      }}>
                        {prediction.structured_formatting.main_text}
                      </div>
                      <div style={{
                        color: '#9ca3af',
                        fontSize: '12px'
                      }}>
                        {prediction.structured_formatting.secondary_text}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div style={{ position: 'relative' }}>
          <div style={{display:'flex', alignItems:'center'}}>
            <Badge count={activeFilters.length} style={{ background: '#06b6d4' }}>
              <Button 
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                style={{ 
                  background: '#1f2937', 
                  border: '1px solid #374151',
                  color: '#06b6d4'
                }}
              >
                {activeFilters.length} filter(s) applied
              </Button>
            </Badge>
            &emsp;
            <Button 
              icon={<Users />}
              style={{ 
                background: '#1f2937', 
                border: '1px solid #374151',
                color: '#06b6d4'
              }}
            >
              {empVisible} Employees
            </Button>
          </div>


          {showFilters && (
            <div style={{ 
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '8px',
              background: '#1f2937',
              borderRadius: '8px',
              padding: '12px',
              width: '320px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
              border: '1px solid #374151'
            }}>
              <div style={{ 
                color: '#e5e7eb', 
                fontSize: '14px', 
                fontWeight: '600',
                marginBottom: '12px',
                paddingBottom: '8px',
                borderBottom: '1px solid #374151'
              }}>
                Filter by Licence Type (Select One)
              </div>
              
              <div style={{height:'auto', maxHeight:'400px', overflow:'auto'}}>

              {
              skillDataLoading?
              <div style={{display:'flex', justifyContent:'center', alignItems:'center'}}>
                <Spinner />
              </div>
              :
              licenceTypes.map(licence => (
                <div 
                  key={licence.id}
                  onClick={() => toggleFilter(licence.id)}
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: activeFilters.includes(licence.id) ? '#374151' : 'transparent',
                    marginBottom: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!activeFilters.includes(licence.id)) {
                      e.currentTarget.style.background = '#2d3748';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!activeFilters.includes(licence.id)) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{ 
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    background: licence.color,
                    flexShrink: 0
                  }} />
                  
                  <span style={{ 
                    color: '#e5e7eb',
                    fontSize: '14px',
                    flex: 1
                  }}>
                    {licence.name}
                  </span>
                  
                  {/* Checkbox icon */}
                  <div style={{ 
                    width: '18px',
                    height: '18px',
                    borderRadius: '4px',  // Square checkbox
                    border: `2px solid ${activeFilters.includes(licence.id) ? '#06b6d4' : '#4b5563'}`,
                    background: activeFilters.includes(licence.id) ? '#06b6d4' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {activeFilters.includes(licence.id) && (
                      <div style={{
                        width: '8px',
                        height: '3px',
                        borderLeft: '2px solid #fff',
                        borderBottom: '2px solid #fff',
                        transform: 'rotate(-45deg)',
                      }} />
                    )}
                  </div>
                </div>

              ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ position: 'absolute', top: '72px', right: '16px', zIndex: 10, display: 'flex', gap: '8px' }}>
        <Button 
          onClick={()=>navigate("/user/employee")}
          className='grid-outlined-btn'
          // style={{ 
          //   background: '#1f2937', 
          //   border: '1px solid #374151',
          //   color: '#e5e7eb'
          // }}
        >
          List View <ArrowUpRightFromSquare/>
        </Button>

        <Button 
          onClick={resetMap}
          className='grid-filed-btn'
          // style={{ 
          //   background: '#1f2937', 
          //   border: '1px solid #374151',
          //   color: '#e5e7eb'
          // }}
        >
          Reset Map
        </Button>
      </div>

      <div ref={mapRef} style={{ flex: 1, width: '100%' }} />
    </div>
  );
};

export default MapDashboard;